// Story 8.2 (ADO-2011), Slice 1 — the shared cart line row (GD-2): the side panel
// (Figma 4981:2221, mobile drawer 4397:12645) and the full cart at /cart (Order
// Review row anatomy, 4976:1638) render every line through this one component.
// Story 8.11 extends it at review. Story 8.2 Slice 2 adds the mobile full-cart
// layout (`pageMobile`, Option A 4379:12439, GD-3): row 1 the 56px thumbnail with
// the name and code, row 3 the stepper and a 40x40 trash icon button.
//
// Anatomy: thumbnail, name (from the basket line, Story 8.2 AC5; "Product no
// longer listed" when the SKU has no catalogue row, but no label when the server's
// name lookup failed, `nameUnavailable`) with the SKU code under it
// (GD-5: the SKU code everywhere), the quantity stepper and Delete (on every row, the phone
// drawer included, as the mobile sheet line 5330:14367 draws it). A shown name links to the
// product's detail page (`/products/:sku`, the route the product card's title opens); a line
// with no name to show has no link. Every step or delete commits at once
// through the basket store (AC4: no save action); the store owns the optimistic
// update, the per-SKU mutex and the row-only rollback, and this row shows that
// SKU's server message inline when an edit fails.
//
// Controls are never disabled while an edit is in flight (disabling the focused
// button would drop focus to <body>). Further steps show at once and the store
// sends only the latest quantity once the in-flight edit settles (AC4); the
// stepper group advertises `aria-busy` meanwhile.
//
// A removal (Delete, or a decrease to zero) hands focus to the host at once,
// because the optimistic removal unmounts the focused button. If the store then
// reports the removal did not land, the row is back on screen with its inline
// error, and focus returns to the control the Shopper used on it (AC4), unless
// they have already moved focus somewhere else.
//
// The quantity is also a typeable, digits-only field between the steps (SL1.0's cart
// `cart_display_qty` text field, `cabinet_smanager_cart_department.htm:111`, with
// `isNumber`), through the design-system stepper's `editable` mode and the product
// card's `sanitiseQuantityInput`, so it is capped at the line maximum. A typed value
// commits once, on Enter or on blur, through the same `setQuantity` as a step; Escape
// or an unchanged value commits nothing. An emptied field commits nothing either: on
// Enter, blur or Escape the previous quantity comes back (SL1.0 commits nothing until
// "Update Cart", so leaving a cleared field there never removes a line). An explicitly
// typed 0 removes the line, on Enter or blur, as SL1.0's cart update drops every line
// below 1 (`get_cart.php:354`). Enter keeps focus on the field; a removal hands focus
// on like any other, unless the Shopper has already moved focus out of the row.
// Increase is disabled at the line maximum.
//
// Story 8.11 (ADO-2020) adds two OPTIONAL props for the pre-send review, both additive:
// without them the row renders exactly as 8.2 built it (the side panel never passes
// them). `lastOrderedAt` renders the LAST ORDERED cell (Figma 4976:1638; the em-dash
// glyph of 4977:2339 when never ordered), and `discontinued` renders the "Not available /
// discontinued" pill (4963:1537) with a read-only "Qty N" in place of the stepper,
// keeping Delete (4977:1962). No substitute, no note line (gate decisions 4, 5). On
// 8.2's `pageMobile` Option A row (4379:12439, gate decision 6) LAST ORDERED renders as a
// label over its value; the pill follows the code there too, as the newer mobile frame
// 5324:12598 draws it. Availability pills are not built.
//
// A review row (one passed `lastOrderedAt`) carries
// every approved column after LAST ORDERED (Figma header 4977:1745): RSP, PACK/CASE, PRICE
// and MARGIN. PACK/CASE shows the host's `packCase` label (the catalogue's own pack facts);
// PRICE shows the unit price the server locked on the line (Figma
// row 4977:1802), or the glyph named "not available yet" while the line has none; RSP and
// MARGIN have no source, so each always shows that glyph. Each cell names itself with a
// caption: hidden visually on the desktop table, whose column header names it; shown on
// the tablet row (5329:13508) and the mobile card (5324:12598), which have no column
// header. The five cells sit in one `stats` wrapper,
// which the desktop grid sees through (`display: contents`) and the tablet and mobile
// rows lay out as their own grid. The mobile review row's Delete carries its visible label.
//
// Story 8.11 (ADO-2020) user decision (2026-09-25): a third optional prop, `readOnly`,
// renders another contributor's line on the review: the same "Qty N" read-only quantity,
// no Delete, and none of the store's per-SKU busy/error state, which belongs to the
// caller's own line of that SKU. Without it the row is unchanged.
//
// Story 8.12 (ADO-2021): an optional `onOrder` prop renders the on-order chip. On the
// review rows it sits under the code (desktop and tablet) or on its own row (mobile). On the
// side panel row (Cart Drawer Row, Figma 5008:41020) it sits on the row's Details line, between
// Delete and the money, starting under the name; in the phone drawer it has a full-width Meta
// row of its own (5330:14367). Every surface shows the same text, the product card's.
//
// Story 8.10 (ADO-2019), Q7: a line that came from an accepted handheld order carries the grey
// "HHT" pill ("N of M from HHT" when part of it did, `hhtCartLabel`), on every variant and
// for substitutes too. It follows the on-order chip: under it in the review's product column
// (desktop and tablet), on the chip's own row under it on the mobile review card, beside
// Delete on the tray's Details line (wrapping under the chip when both show), and beside the
// chip on the phone drawer's Meta row.
//
// The stepper is Figma `Stepper/Touch` (5334:17305) on the phone rows and `adaptive` on the
// page and panel rows, which span tablet (Touch) and desktop (`Stepper/Default`, 18:15).
//
// The side panel rows (`panel`, `panelMobile`; Figma Cart Drawer Row 5008:41020, tablet tray
// 5330:15683, phone sheet 5330:14367) show the line's money: its value (`lineValue`: the
// locked line total, else unit price times quantity) over the unit price per case,
// right-aligned. A line with no locked price yet (an optimistic add the server has not
// answered) shows UNKNOWN_TOTAL for the value and no unit price. The drawer row's margin and
// offer lines have no source and are not drawn.
//
// ADO-2246: a panel row with a locked deposit above zero also shows "Deposit €X" (the rate per
// case), small and muted, in the Details line beside Delete (tray; above the on-order chip or HHT
// pill when the line has one, ADO-2249 D2) or in the left of the money row (phone sheet), as the
// Cart Side Panel frames draw it; none for a line with no deposit. A deposit that could not be
// read (the field is absent) shows "Deposit —" with the empty-cell glyph, never "Deposit €0.00".
// The money block itself is unchanged: the deposit is never part of the line value or the unit
// price.
//
// ADO-2250: a review row (`page`, `pageMobile` with `lastOrderedAt`) draws the same deposit
// text, by the same rule (`showsLineDeposit`, `LineDeposit`), on its own line under the PRICE
// value inside the PRICE cell, as the Order Review frames draw it at every breakpoint. The
// PRICE value itself is unchanged.
//
// ADO-2263: the thumbnail tile shows the product image through the listing cards'
// `ProductImage` (the branded placeholder when there is no image or it fails to load), with
// the product name as its alt (the SKU code when the line has no name). The image comes from
// the line's `imageUrl`, or from the host's `imageUrl` prop, which overrides it the way `name`
// does (the review host builds its own line). Each variant keeps its tile size and radius,
// so the row height never depends on the image's load state. An optimistic line has no image
// yet and shows the placeholder until the server answers.
//
// Pricing migration 18 (ADO-2249, Task 9.1; Figma set 3, DEC-13 a): the line explains its price
// from its stored `priceKind` and `wasPrice` only (DEC-6). A promotion line carries the
// `Promotion` pill (red tint, tag icon), `Save €X` in red ((was − unit) × quantity, AC5), and its
// unit price in red with the was price struck: after the unit price on the panel rows, above the
// price in the review's PRICE cell. The struck figure is hidden from assistive tech and the pair is
// read "Was €34.00, now €29.00 per case" (DEC-15). A contract line carries the `Contract price`
// pill (Figma set 4b) and nothing else (no data for a contract saving). A standard or WOW line
// (DEC-9), and a line the server sent no `priceKind` for, carries no pill. With
// VITE_REAL_PRICES_ENABLED off a line explains nothing (DEC-18). Panel rows put the pill
// and the saving in the Details line's middle column, above the deposit and the on-order chip (the
// phone drawer: on the left of the money row); review rows put the pill after the code, and the
// saving under the PRICE value.
import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  ConflictMessage,
  Icon,
  QuantityStepper,
  StatusBadge,
  type QuantityStepperCommit,
} from '../design-system';
import { formatSubmittedDate } from '../shared/datetime';
import { formatMoney, type MoneyValue } from '../shared/money';
import type { BasketLine } from '../api/basket.client';
import type { OnOrderProduct } from '../api/on-order.client';
import { OnOrderChip } from '../onOrder/OnOrderChip';
import { hhtCartLabel } from './hhtCartMarker';
import { ProductImage } from '../app/Browse/ProductImage';
import { isAtLineMaximum, sanitiseQuantityInput, typedLineQuantity } from './lineQuantity';
import { UNKNOWN_SAVING, UNKNOWN_TOTAL, explainedPriceKind, lineSaving } from './cartTotals';
import { ContractPricePill } from '../pricing/ContractPricePill';
import { spokenWasNow } from '../pricing/priceExplanation';
import { lineValue } from './cartMoney';
import { useBasketStore } from './useBasketStore';
import { conflictView, type ConflictIntent } from './conflictCopy';
import styles from './CartLineRow.module.css';

/** The control a removal was made with, so the host can move focus to the same
 *  control on the neighbouring row (AC4). */
export type RowControl = 'decrease' | 'delete' | 'quantity';

/** Story 8.2 (ADO-2011), AC5 — shown for a line whose SKU has no catalogue row. */
export const PRODUCT_NO_LONGER_LISTED = 'Product no longer listed';

/** Story 8.2 (ADO-2011), AC5 — whether a line's own name reads as "Product no longer
 *  listed": the server sent `null` (no catalogue row) and not because its name lookup
 *  failed (`nameUnavailable`, ADO #2011 comment 7282179), which shows the code only. */
export function isNoLongerListed(line: Pick<BasketLine, 'name' | 'nameUnavailable'>): boolean {
  return line.name === null && line.nameUnavailable !== true;
}

/** Focus `control` on the row for `skuCode` inside `scope` (the side panel dialog
 *  or the /cart main, captured before the row unmounted). A no-op when the row is
 *  not on screen. */
export function focusRowControl(scope: ParentNode, skuCode: string, control: RowControl): void {
  const row = Array.from(scope.querySelectorAll<HTMLElement>('li[data-sku]')).find(
    (element) => element.dataset['sku'] === skuCode,
  );
  row?.querySelector<HTMLElement>(`[data-control="${control}"]`)?.focus();
}

export type CartLineRowVariant = 'panel' | 'panelMobile' | 'page' | 'pageMobile';

export interface CartLineRowProps {
  readonly line: BasketLine;
  /** Product description, overriding the line's own `name` (the basket state
   *  carries it since Story 8.2 Slice 2; a host such as 8.11 may supply its own). */
  readonly name?: string | null;
  /** ADO-2263: the product image URL, overriding the line's own `imageUrl` (the review
   *  host builds a fresh line without it). `null` shows the placeholder. */
  readonly imageUrl?: string | null;
  readonly variant: CartLineRowVariant;
  /** Called with the SKU and the control used as a removal is committed, so the
   *  host list can move focus (to the neighbouring row) before this row unmounts
   *  (AC4). If the removal then fails, the row takes focus back once restored. */
  readonly onFocusAfterRemove: (skuCode: string, control: RowControl) => void;
  /** Story 8.11: when PASSED, render the LAST ORDERED cell: a UTC ISO instant shows as
   *  DD/MM/YYYY in the viewer's zone; `null` shows the never-ordered glyph. Omitted, no
   *  cell is rendered. */
  readonly lastOrderedAt?: string | null;
  /** Story 8.11: with `lastOrderedAt`, the date could not be read (the lookup failed):
   *  the cell shows nothing rather than the never-ordered glyph, which would be a false
   *  claim. The host explains the blank cells once for the page. */
  readonly lastOrderedUnknown?: boolean;
  /** Story 8.11: the product was discontinued after the line was added. */
  readonly discontinued?: boolean;
  /** Story 8.11: another contributor's line (8.2's store does not hold it): show the
   *  quantity read-only, with no stepper, no Delete and no store error. */
  readonly readOnly?: boolean;
  /** With `readOnly`: the basket never loaded, so the page cannot tell whose line this
   *  is. The quantity is then named for its SKU only, never as another contributor's. */
  readonly ownershipUnknown?: boolean;
  /** With `lastOrderedAt`: the PACK/CASE cell's label (the catalogue's pack facts); `null`
   *  or omitted shows the glyph named "not recorded". */
  readonly packCase?: string | null;
  /** Story 8.12: the Customer's open orders holding this product, rendered as the on-order
   *  chip on every variant; omitted, no chip. */
  readonly onOrder?: OnOrderProduct;
  /** Story 8.3 (ADO-2012), Task 11.1: the "Conflict" option. On, the row shows the Conflict
   *  Message for a refused change to its line (cards 02, 04 and 05); off (the default), it
   *  shows none, so a host that does not opt in is unchanged. */
  readonly conflict?: boolean;
  /** Called when the product name link is clicked, before the navigation: the side panel
   *  closes itself, since a move between two detail pages keeps the same header mounted. */
  readonly onProductLinkClick?: () => void;
}

/** The never-ordered glyph (Figma 4977:2339, mobile 4394:36), also the empty-cell glyph
 *  of the review's RSP, PACK/CASE, PRICE and MARGIN cells and of the product page's Last
 *  Ordered cell. */
export const NEVER_ORDERED_GLYPH = '—';

/** What an RSP, PRICE or MARGIN placeholder (and the product page's Last Ordered one) says
 *  to assistive tech. */
export const NOT_AVAILABLE_YET = 'not available yet';
/** What an empty PACK/CASE cell says to assistive tech. */
const PACK_CASE_NOT_RECORDED = 'not recorded';
/** Story 8.11 (ADO-2020), AC2 — the discontinued pill copy (Figma 4963:1537). */
export const DISCONTINUED_PILL = 'Not available / discontinued';

export function CartLineRow({
  line,
  name,
  imageUrl,
  variant,
  onFocusAfterRemove,
  lastOrderedAt,
  lastOrderedUnknown = false,
  discontinued = false,
  readOnly = false,
  ownershipUnknown = false,
  packCase,
  onOrder,
  conflict = false,
  onProductLinkClick,
}: CartLineRowProps) {
  const { skuCode, quantity } = line;
  const showsOnOrder = onOrder !== undefined;
  const hhtLabel = hhtCartLabel(line);
  const showsMarkers = showsOnOrder || hhtLabel !== null;
  const setQuantity = useBasketStore((s) => s.setQuantity);
  const remove = useBasketStore((s) => s.remove);
  const busy = useBasketStore((s) => s.pending.has(skuCode));
  const error = useBasketStore((s) => s.mutationErrors.get(skuCode));
  const lineConflict = useBasketStore((s) => (conflict ? s.lineConflicts.get(skuCode) : undefined));
  // Only a line that is still there shows its message here; a removed line is the host
  // list's Removed Line (card 03).
  const shownConflict = lineConflict === undefined || readOnly ? null : conflictView(lineConflict);
  const conflictMessage = shownConflict?.kind === 'changed' ? shownConflict : null;
  const conflictAction = conflictMessage?.action ?? null;
  const showDelete = !readOnly;
  // The trash is icon-only on 8.2's mobile full-cart row (Figma 4379:12439); the mobile
  // review row draws it with its label (5324:12598).
  const iconOnlyDelete = variant === 'pageMobile' && lastOrderedAt === undefined;
  const resolvedName = name !== undefined ? name : line.name;
  const hasName = resolvedName !== undefined && resolvedName !== null && resolvedName !== '';
  const resolvedImageUrl = (imageUrl !== undefined ? imageUrl : line.imageUrl) ?? null;
  // The image is named for the product; a line with no name falls back to its code.
  const imageAlt =
    typeof resolvedName === 'string' && resolvedName.trim() !== '' ? resolvedName : skuCode;
  // `null` is the server's "no catalogue row for this SKU"; `undefined` is an
  // optimistic line the server has not answered for yet (no label either way). A
  // `null` from a FAILED lookup (`nameUnavailable`, ADO #2011 comment 7282179) is
  // not a delisting, so it shows the code only. A host-supplied `name` overrides
  // the line's own name and its lookup flag together.
  const noLongerListed = name === undefined ? isNoLongerListed(line) : name === null;
  const showsLastOrdered = lastOrderedAt !== undefined;
  const mobile = variant === 'pageMobile';
  const panel = variant === 'panel' || variant === 'panelMobile';
  // ADO-2250: a review row draws the same deposit text, under its PRICE value.
  const showsDeposit = (panel || showsLastOrdered) && showsLineDeposit(line, busy);
  const explanation = priceExplanationOf(line);
  const priceTypePill =
    explanation.priceType === 'promotion' ? (
      <span className={styles.promotionPill} data-price-type="promotion">
        <Icon name="tag" size={12} />
        Promotion
      </span>
    ) : explanation.priceType === 'contract' ? (
      <span className={styles.priceTypePill} data-price-type="contract">
        <ContractPricePill />
      </span>
    ) : null;
  const savingText =
    explanation.saving === null ? null : (
      <span className={styles.saving} data-part="saving">
        Save {formatMoney(explanation.saving)}
      </span>
    );
  const rowRef = useRef<HTMLLIElement>(null);

  const commitRemoval = async (edit: () => Promise<boolean>, control: RowControl) => {
    const scope = rowRef.current?.closest('[role="dialog"], main') ?? document;
    const landed = edit();
    onFocusAfterRemove(skuCode, control);
    const handedTo = document.activeElement;
    if (await landed) return;
    const focusIsStillHandedOff =
      document.activeElement === handedTo || document.activeElement === document.body;
    if (focusIsStillHandedOff) focusRowControl(scope, skuCode, control);
  };

  const decrease = () => {
    const next = quantity - 1;
    if (next <= 0) {
      void commitRemoval(() => setQuantity(skuCode, 0), 'decrease');
      return;
    }
    void setQuantity(skuCode, next);
  };
  const increase = () => {
    void setQuantity(skuCode, quantity + 1);
  };
  const deleteLine = () => {
    void commitRemoval(() => remove(skuCode), 'delete');
  };
  const commitTyped = ({ typed, trigger, focusTarget }: QuantityStepperCommit) => {
    const next = typedLineQuantity(typed, quantity);
    if (next === null) return;
    if (next > 0) {
      void setQuantity(skuCode, next);
      return;
    }
    const focusLeftTheRow =
      trigger === 'blur' && focusTarget !== null && !rowRef.current?.contains(focusTarget);
    if (focusLeftTheRow) {
      void setQuantity(skuCode, 0);
      return;
    }
    void commitRemoval(() => setQuantity(skuCode, 0), 'quantity');
  };

  // The message's action is a new change to the line, which ends the message (the lifetime
  // rule). Focus moves to the row's own control first, so it is not lost with the button.
  const runConflictAction = (intent: ConflictIntent) => {
    if (intent.type === 'remove') {
      void commitRemoval(() => remove(skuCode), 'delete');
      return;
    }
    rowRef.current?.querySelector<HTMLElement>('[data-control="quantity"]')?.focus();
    void setQuantity(skuCode, intent.quantity);
  };

  const deleteButton = showDelete ? (
    <button
      type="button"
      className={styles.deleteButton}
      aria-label={`Delete ${skuCode} from the cart`}
      data-control="delete"
      onClick={deleteLine}
    >
      <Icon name="trash" size={iconOnlyDelete ? 16 : 14} />
      {iconOnlyDelete ? null : <span aria-hidden>Delete</span>}
    </button>
  ) : null;

  const hhtPill = (className: string | undefined) =>
    hhtLabel === null ? null : (
      <span
        className={className === undefined ? styles.hhtPill : `${styles.hhtPill} ${className}`}
        data-hht-pill=""
      >
        {hhtLabel}
      </span>
    );
  // The on-order chip and the HHT pill, for the rows that give them one place together.
  const markers = (
    chipClassName: string | undefined,
    groupClassName: string,
    hhtClassName: string | undefined,
  ) => {
    if (showsOnOrder && hhtLabel !== null) {
      return (
        <span className={groupClassName} data-part="markers">
          <OnOrderChip product={onOrder} className={chipClassName} />
          {hhtPill(undefined)}
        </span>
      );
    }
    if (showsOnOrder) return <OnOrderChip product={onOrder} className={chipClassName} />;
    return hhtPill(hhtClassName);
  };

  return (
    <li
      ref={rowRef}
      className={styles.row}
      data-variant={variant}
      data-sku={skuCode}
      data-review={showsLastOrdered ? '' : undefined}
      data-on-order={showsOnOrder ? '' : undefined}
      data-markers={showsMarkers ? '' : undefined}
      data-deposit={showsDeposit ? '' : undefined}
      data-conflict={conflictMessage === null ? undefined : ''}
      data-explained={panel && (priceTypePill !== null || savingText !== null) ? '' : undefined}
    >
      <div className={styles.thumbnail} data-part="thumbnail">
        <ProductImage src={resolvedImageUrl} alt={imageAlt} />
      </div>
      <div className={styles.identity} data-part="identity">
        {hasName ? (
          <span className={styles.name}>
            <Link
              className={styles.nameLink}
              to={`/products/${encodeURIComponent(skuCode)}`}
              onClick={onProductLinkClick}
            >
              {resolvedName}
            </Link>
          </span>
        ) : null}
        {noLongerListed ? (
          <span className={styles.notListed}>{PRODUCT_NO_LONGER_LISTED}</span>
        ) : null}
        {discontinued ? (
          // The pill follows the code at every width (desktop 4977:1962; the mobile card
          // 5324:12598 "Code + stock", which supersedes Option A's pill-first slot).
          <span className={styles.codeRow} data-part="code-row">
            <span className={styles.code} data-part="code">
              {skuCode}
            </span>
            <StatusBadge tone="neutral">{DISCONTINUED_PILL}</StatusBadge>
            {/* ADO-2249: the price-type pill follows, so the red price and saving stay explained. */}
            {!panel ? priceTypePill : null}
          </span>
        ) : !panel && priceTypePill !== null ? (
          <span className={styles.codeRow} data-part="code-row">
            <span className={styles.code} data-part="code">
              {skuCode}
            </span>
            {priceTypePill}
          </span>
        ) : (
          <span className={styles.code} data-part="code">
            {skuCode}
          </span>
        )}
        {/* Story 8.12 (ADO-2021): under the code and stock line, above Delete (Figma 4977:1802). */}
        {showsOnOrder && !mobile && !panel ? (
          <OnOrderChip product={onOrder} className={styles.onOrder} />
        ) : null}
        {/* Q7: under the code and the chip, above Delete. */}
        {!mobile && !panel ? hhtPill(styles.hhtInIdentity) : null}
      </div>
      {/* The stacked mobile card gives the chip its own full-width row under the thumbnail and
          name, as Figma 5346:55453 draws it, instead of wrapping it beside the thumbnail; the
          HHT pill goes under it on that row. */}
      {mobile && showsMarkers
        ? markers(
            styles.onOrderRow,
            `${styles.onOrderRow} ${styles.markersStack}`,
            styles.onOrderRow,
          )
        : null}
      {showsLastOrdered ? (
        <div className={styles.stats} data-part="stats">
          <ReviewCell cell="last-ordered" label="Last ordered">
            {lastOrderedAt !== null && lastOrderedAt !== '' ? (
              <time className={styles.cellValue} dateTime={lastOrderedAt} data-part="value">
                {formatSubmittedDate(lastOrderedAt)}
              </time>
            ) : (
              <span className={styles.cellValue} data-part="value">
                {lastOrderedUnknown ? '' : NEVER_ORDERED_GLYPH}
              </span>
            )}
          </ReviewCell>
          <ReviewCell cell="rsp" label="RSP">
            <EmptyValue spoken={NOT_AVAILABLE_YET} />
          </ReviewCell>
          <ReviewCell cell="pack-case" label="Pack/case">
            {packCase !== undefined && packCase !== null && packCase !== '' ? (
              <span className={styles.cellValue} data-part="value">
                {packCase}
              </span>
            ) : (
              <EmptyValue spoken={PACK_CASE_NOT_RECORDED} />
            )}
          </ReviewCell>
          <ReviewCell cell="price" label="Price">
            {line.unitPrice !== undefined ? (
              <>
                {explanation.wasPrice !== null ? (
                  <s className={styles.was} aria-hidden="true">
                    {formatMoney(explanation.wasPrice)}
                  </s>
                ) : null}
                <span
                  className={`${styles.cellValue} ${styles.price}${
                    explanation.priceType === 'promotion' ? ` ${styles.promotion}` : ''
                  }`}
                  data-part="value"
                  aria-hidden={explanation.spoken === null ? undefined : 'true'}
                >
                  {formatMoney(line.unitPrice)}
                </span>
                {explanation.spoken === null ? null : (
                  <span className={styles.visuallyHidden}>{explanation.spoken}</span>
                )}
                {savingText}
              </>
            ) : (
              <EmptyValue spoken={NOT_AVAILABLE_YET} />
            )}
            {showsDeposit ? <LineDeposit line={line} /> : null}
          </ReviewCell>
          <ReviewCell cell="margin" label="Margin">
            <EmptyValue spoken={NOT_AVAILABLE_YET} />
          </ReviewCell>
        </div>
      ) : null}
      {discontinued || readOnly ? (
        // Named for its SKU like the stepper group it replaces. Another contributor's
        // line also says whose it is, so it never shares a name with the caller's own
        // line of the same SKU, unless ownership is unknown (the basket never loaded).
        <span
          className={styles.readOnlyQuantity}
          role="group"
          aria-label={
            readOnly && !ownershipUnknown
              ? `Quantity for ${skuCode} (another contributor)`
              : `Quantity for ${skuCode}`
          }
        >
          Qty {quantity}
        </span>
      ) : (
        <QuantityStepper
          className={styles.stepper}
          value={quantity}
          label={`Quantity for ${skuCode}`}
          decreaseLabel={`Decrease quantity of ${skuCode}`}
          increaseLabel={`Increase quantity of ${skuCode}`}
          onDecrease={decrease}
          onIncrease={increase}
          increaseDisabled={isAtLineMaximum(quantity)}
          busy={busy}
          size={mobile || variant === 'panelMobile' ? 'touch' : 'adaptive'}
          decreaseControl="decrease"
          editable={{
            inputLabel: `Quantity of ${skuCode}`,
            sanitiseInput: sanitiseQuantityInput,
            onCommit: commitTyped,
            control: 'quantity',
          }}
        />
      )}
      {panel ? (
        // The Cart Drawer Row's Details line (Figma 5008:41020): Delete, the chip and the money.
        // The tray lays it out as one line; the phone drawer (5330:14367) places each part on
        // its own row of the row grid. Delete keeps this parent at every width, because
        // re-parenting the focused button across the 768px switch would drop focus to <body>.
        <div className={styles.details} data-part="details">
          {deleteButton}
          {priceTypePill !== null || savingText !== null ? (
            <span className={styles.priceType} data-part="price-type">
              {priceTypePill}
              {savingText}
            </span>
          ) : null}
          {showsMarkers
            ? markers(
                styles.onOrderPanel,
                `${styles.onOrderPanel} ${styles.markersLine}`,
                styles.panelHht,
              )
            : null}
          {showsDeposit ? <LineDeposit line={line} /> : null}
          <PanelMoney line={line} explanation={explanation} />
        </div>
      ) : (
        deleteButton
      )}
      {conflictMessage === null ? null : (
        <ConflictMessage
          className={styles.conflict}
          message={conflictMessage.message}
          // Story 8.3 (ADO-2012), card 09: a refused send's marks are announced by its banner.
          announce={lineConflict?.action !== 'review'}
          {...(conflictAction === null
            ? {}
            : {
                action: {
                  label: conflictAction.label,
                  onClick: () => runConflictAction(conflictAction.intent),
                },
              })}
        />
      )}
      {error !== undefined && !readOnly ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
}

/** ADO-2246: whether a panel row (and, ADO-2250, a review row) draws a deposit text: the locked
 *  rate is above zero, or it is unknown (absent). A known zero draws nothing, and neither does a
 *  line still awaiting its first answer (its own write is in flight and it has no locked price
 *  yet), as the side panel's totals leave it out, so a new line does not flash a dash before the
 *  server answers. That case does not arise on a review row: the /cart page reads the review only
 *  once the store has settled, so its rows always carry the server's answer. */
function showsLineDeposit(line: BasketLine, writeInFlight: boolean): boolean {
  if (line.depositPerUnit !== undefined) return line.depositPerUnit.amount > 0;
  const awaitingFirstAnswer = writeInFlight && line.unitPrice === undefined;
  return !awaitingFirstAnswer;
}

/** "Deposit €3.60": the line's locked deposit per case, in the panel row's Details line
 *  (ADO-2246) or under the review row's PRICE value (ADO-2250). An unknown deposit reads
 *  "Deposit —", the glyph hidden from assistive tech, which hears "not available" (the
 *  `EmptyValue` pattern), never a zero. */
function LineDeposit({ line }: { readonly line: BasketLine }) {
  return (
    <span className={styles.deposit} data-part="deposit">
      Deposit{' '}
      {line.depositPerUnit === undefined ? (
        <>
          <span aria-hidden="true">{NEVER_ORDERED_GLYPH}</span>
          <span className={styles.visuallyHidden}>not available</span>
        </>
      ) : (
        formatMoney(line.depositPerUnit)
      )}
    </span>
  );
}

/** What a line's stored figures say about its price (ADO-2249, DEC-6): which pill it carries,
 *  its saving, the was price to strike, and the words a screen reader hears for the pair. */
interface PriceExplanation {
  readonly priceType: 'promotion' | 'contract' | null;
  readonly saving: MoneyValue | null;
  readonly wasPrice: MoneyValue | null;
  readonly spoken: string | null;
}

function priceExplanationOf(line: BasketLine): PriceExplanation {
  // Nothing while VITE_REAL_PRICES_ENABLED is off (DEC-18).
  const priceKind = explainedPriceKind(line);
  const priceType = priceKind === 'promotion' || priceKind === 'contract' ? priceKind : null;
  const saving = lineSaving(line);
  const known = saving === null || saving === UNKNOWN_SAVING ? null : saving;
  // Only a was price above the price is struck, so the strike and the saving always agree.
  const wasPrice = known !== null && line.wasPrice !== undefined ? line.wasPrice : null;
  const spoken =
    wasPrice !== null && line.unitPrice !== undefined
      ? spokenWasNow(wasPrice, line.unitPrice, ' per case')
      : null;
  return { priceType, saving: known, wasPrice, spoken };
}

/** The side panel row's money: the line's value over its locked unit price per case. A
 *  promotion's unit price is red, with its was price struck after it (ADO-2249, Figma set 3). */
function PanelMoney({
  line,
  explanation,
}: {
  readonly line: BasketLine;
  readonly explanation: PriceExplanation;
}) {
  const value = lineValue(line);
  return (
    <div className={styles.money} data-part="money">
      <span className={styles.lineValue} data-part="line-value">
        {value === null ? UNKNOWN_TOTAL : formatMoney(value)}
      </span>
      {line.unitPrice !== undefined ? (
        <span className={styles.unitPriceLine}>
          <span
            className={styles.unitPriceFigures}
            aria-hidden={explanation.spoken === null ? undefined : 'true'}
          >
            <span
              className={
                explanation.priceType === 'promotion'
                  ? `${styles.unitPrice} ${styles.promotion}`
                  : styles.unitPrice
              }
              data-part="unit-price"
            >
              {formatMoney(line.unitPrice)}/case
            </span>
            {explanation.wasPrice !== null ? (
              <s className={styles.was}>{formatMoney(explanation.wasPrice)}</s>
            ) : null}
          </span>
          {explanation.spoken === null ? null : (
            <span className={styles.visuallyHidden}>{explanation.spoken}</span>
          )}
        </span>
      ) : null}
    </div>
  );
}

/** One labelled review cell: its caption, then its value. */
function ReviewCell({
  cell,
  label,
  children,
}: {
  readonly cell: string;
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className={styles.cell} data-cell={cell}>
      <span className={styles.cellLabel} data-part="label">
        {label}
      </span>
      {children}
    </div>
  );
}

/** The empty-cell glyph, hidden from assistive tech, which hears `spoken` instead. */
function EmptyValue({ spoken }: { readonly spoken: string }) {
  return (
    <span className={styles.cellValue} data-part="value">
      <span aria-hidden="true">{NEVER_ORDERED_GLYPH}</span>
      <span className={styles.visuallyHidden}>{spoken}</span>
    </span>
  );
}
